import {
  Client,
  lookupPostcode,
  UkAddress,
  autocomplete,
  udprn,
} from "@ideal-postcodes/core-browser";
import axios from "axios";

const apiKey = process.env.REACT_APP_IDEAL_POSTCODES_API_KEY || "";
const client = new Client({ api_key: apiKey });

interface IPostcodeResults {
  status: number;
  result: string[];
}

export interface AutocompleteSuggestion {
  suggestion: string;
  id: string;
  udprn?: number | string;
  urls?: {
    id?: string;
    udprn?: string;
  };
}

const addressCache = new Map<string, UkAddress[]>();

function extractUdprn(value: AutocompleteSuggestion | string | number): string {
  if (typeof value === "string" || typeof value === "number") {
    return value.toString();
  }

  if (value.udprn) return value.udprn.toString();

  const url = value.urls?.udprn || value.urls?.id || value.id;
  const match = url.match(/(\d+)$/);
  return match ? match[1] : url;
}

export const AddressSearchService = {
  async getPostcodeSuggestions(partialPostcode: string): Promise<string[]> {
    if (!partialPostcode || partialPostcode.length < 2) return [];

    let postcodeResults: string[] = [];

    await axios
      .get(`https://api.postcodes.io/postcodes/${partialPostcode}/autocomplete`)
      .then((result: { data: IPostcodeResults }) => {
        postcodeResults = result.data.result ?? [];
      })
      .catch((result) => {
        console.error(result);
        postcodeResults = [];
      });

    return postcodeResults;
  },

  async getAddressesForPostcode(postcode: string): Promise<UkAddress[]> {
    if (!postcode) return [];

    const formattedPostcode = postcode.toUpperCase().trim();

    if (addressCache.has(formattedPostcode))
      return addressCache.get(formattedPostcode)!;

    try {
      const results = await lookupPostcode({
        client,
        postcode: formattedPostcode,
      });

      addressCache.set(formattedPostcode, results);
      return results;
    } catch (error: any) {
      if (error.status === 404) return [];

      console.error("Full address lookup failed:", error);
      throw error;
    }
  },

  async getAddressAutocomplete(value: string): Promise<AutocompleteSuggestion[]> {
    const query = value.trim();
    if (query.length < 2 || !apiKey) return [];

    try {
      const response = await autocomplete.list(client, {
        query: {
          query,
          api_key: apiKey,
        },
      });

      const results = response.body.result.hits as AutocompleteSuggestion[];
      return results;
    } catch (error) {
      console.error("Address autocomplete failed:", error);
      return [];
    }
  },

  async get(value: string): Promise<AutocompleteSuggestion[]> {
    return AddressSearchService.getAddressAutocomplete(value);
  },

  async resolveAddress(
    suggestion: AutocompleteSuggestion | string | number
  ): Promise<UkAddress | null> {
    try {
      const udprnId = extractUdprn(suggestion);
      if (!udprnId || !apiKey) return null;

      const response = await udprn.retrieve(client, udprnId, {
        query: { api_key: apiKey },
      });
      return response.body.result as UkAddress;
    } catch (error) {
      console.error("Address resolve failed:", error);
      return null;
    }
  },
};
